import React, { JSX, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';

// import { selectInspectionById } from '@/utils/inspection.utils';
import { ModuleCard } from './components/module-card/ModuleCard';
import * as Components from './components';
import { Footer } from './components/footer/footer';
import styles from './InspectionDetails.module.scss';
import { InspectionDefectChange } from '@/types/ModuleComponentProp.type';
import { useOpenInspection } from '@/hooks/useOpenInspection';
import { submitOpenedInspection } from '@/store/thunk/Inspection.thunk';
import { setPageHeader } from '@/store/slices/appDataSlice';
import { store } from '@/store/store';
import { useTemplate } from '@/hooks/useTemplate';
import { useTranslation } from "react-i18next";
import { toast } from 'react-toastify';
import { ROUTES } from '@/route/routes.constants';
import {
    closeInspection,
    updateOpenedInspection,
    clearOpenInspectionLoadError,
} from '@/store/slices/openInspectionSlice';
import { InspectionModule } from '@/types/Template.type';
import { SaveChangesModal } from './components/saveChangeCard/SaveChangesModal';
import { Preview } from './components/preview/Preview';
import { InspectionDefect } from '@/types/InspectionDefect';
import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';
import { useNavigationContext } from '@/hooks/useNavigationContext';
import { useInspectionGpsCapture } from '@/hooks/useInspectionGpsCapture';
import { ActionButtons } from './ActionButtons';
import { LockedForUploadMessage } from './components/locked-for-upload-message/LockedForUploadMessage';
import { useCanOpenInspection } from '@/hooks/useCanOpenInspection';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { ROLE } from '@/role.constant';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { RootState } from '@/store/rootReducer';
import { APP_CONFIG_KEY } from '@/types/AppConfig.type';

export const InspectionDetails: React.FC = () => {
    const { t: translate } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { inspection: currentInspection, isModified } = useOpenInspection(id);
    const openLoadError = useAppSelector((state: RootState) => state.openedInspection.loadError);
    const template = useTemplate(currentInspection);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [previewOpen, setPreviewOpen] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const { setIsBlocked, setOnBlockedNavigation } = useNavigationContext();
    const canOpenInspection = useCanOpenInspection(id);
    const { hasRole } = useRoleAccess();
    const startInspectionCheck = useAppSelector((state: RootState) => state.appConfig[APP_CONFIG_KEY.startInspectionCheck]);

    useInspectionGpsCapture(id);

    const skipStartWorkFlowRef = useRef(!!location.state?.skipStartWorkFlow);

    useEffect(() => {
        store.dispatch(setPageHeader('Inspection'));
    }, []);

    /** Inspectors: show load failure on start page; admins: inline message below. */
    useLayoutEffect(() => {
        if (!id || !openLoadError || openLoadError.inspectionId !== id) {
            return;
        }
        if (hasRole(ROLE.ADMINISTRATOR)) {
            return;
        }
        dispatch(clearOpenInspectionLoadError());
        navigate(ROUTES.INSPECTION_START.replace(':id', id), {
            replace: true,
            state: { openInspectionHydrationError: openLoadError.reason },
        });
    }, [id, openLoadError, hasRole, navigate, dispatch]);

    // Ensure inspection is opened via the precheck page for non-administrator users.
    // skipStartWorkFlowRef is captured once on mount so re-renders cannot lose the flag.
    useEffect(() => {
        if (skipStartWorkFlowRef.current) return;
        const isAdministrator = hasRole(ROLE.ADMINISTRATOR);

        if (startInspectionCheck && !isAdministrator) {
            if (id) {
                navigate(ROUTES.INSPECTION_START.replace(':id', id), { replace: true });
            } else {
                navigate(ROUTES.INSPECTIONS, { replace: true });
            }
        }
    }, [id, navigate, hasRole, startInspectionCheck]);

    // Update global navigation blocking state when local state changes
    useEffect(() => {
        setIsBlocked(isModified);
        setOnBlockedNavigation(() => setOpenDialog);
    }, [isModified, setIsBlocked, setOnBlockedNavigation]);

    // Block navigation when there are unsaved changes
    useNavigationBlocker({
        isBlocked: isModified,
        onBlockedNavigation: () => setOpenDialog(true),
        message: 'You have unsaved changes. Are you sure you want to leave?'
    });

    const handleSubmitClick = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            if (isModified) {
                await store.dispatch(submitOpenedInspection({ id: currentInspection?.id }));
                toast.success(translate('Inspection_Submitted_Successfully'));
            }else{
                 store.dispatch(closeInspection(currentInspection?.id || ''));

            }
            navigate(ROUTES.INSPECTIONS);
        } catch (error) {
            console.error("Error submitting inspection:", error);
            toast.error(translate('Inspection_Submission_Failed') || 'Failed to submit inspection');
        } finally {
            setIsSubmitting(false);
        }
    }
    const handlePreviewClick = () => {
        setPreviewOpen(true);
    };
    const onValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {

        let { name, value, type } = event.target;

        let castedValue: string | number | boolean;

        // Determine the value type based on the input type
        switch (type) {
            case 'number':
                castedValue = parseFloat(value);
                if (isNaN(castedValue)) castedValue = 0; // Fallback for invalid numbers
                break;
            default:
                castedValue = value; // Defaults to string
        }
        store.dispatch(updateOpenedInspection({ id: currentInspection?.id, [name]: castedValue }));
    }
    const defectsHandler: InspectionDefectChange = {
        onAdd: (defect: InspectionDefect) => {
            const createdOn = new Date().toISOString();
            const newDefect: InspectionDefect = {
                ...defect,
                isNew: true,
                createdOn,
                defectId: defect?.defectCode?.id || '',
                inspectionDataId: currentInspection?.id || defect.inspectionDataId
            };
            store.dispatch(updateOpenedInspection(
                {
                    id: currentInspection?.id,
                    qcDefects: [...currentInspection.qcDefects, newDefect]
                }));
        },
        onDelete: (newDefectId: string) => {
            store.dispatch(updateOpenedInspection(
                {
                    id: currentInspection?.id,
                    qcDefects: [...currentInspection.qcDefects.filter(d => d.newId !== newDefectId && d.id !== newDefectId)]
                }));
        },
        onEdit: (defect: InspectionDefect) => {
            defect.inspectionDataId = currentInspection?.id || defect.inspectionDataId;
            let filter: (d: InspectionDefect) => InspectionDefect = d => d.id === defect.id ? defect : d;
            if (!defect.id) {
                filter = d => d.newId === defect.newId ? defect : d;
            }
            store.dispatch(updateOpenedInspection(
                {
                    id: currentInspection?.id,
                    qcDefects: [...currentInspection.qcDefects.map(filter)],
                }));
        }

    }

    const getComponent = (module: InspectionModule): JSX.Element => {
        const Component = Components[module.name as keyof typeof Components];
        return Component ? <Component inspection={currentInspection} structure={module} onValueChange={onValueChange} defectsChangeHandlers={defectsHandler} /> : <div>Component `{module.name}` not found</div>;
    }

    const renderInspectionModule = (module: InspectionModule) => {
        switch (module.type) {
            case 'component':
                return getComponent(module);
            case 'module':
                return <ModuleCard inspection={currentInspection} structure={module} defectsChangeHandlers={defectsHandler} />
            default:
                return <div>Unknown module type {module.type}</div>
        }
    }

    const handleBackClick = () => {
        if (isModified) {
            setOpenDialog(true);
        } else {
            store.dispatch(closeInspection(currentInspection?.id || ''));
            navigate(ROUTES.INSPECTIONS);
        }
    }

    const handleDiscardChanges = () => {
        setOpenDialog(false);
        if (currentInspection) {
            store.dispatch(closeInspection(currentInspection?.id || ''  ));
        }
        navigate(ROUTES.INSPECTIONS);
    }
    const hasInvalidRemovedQuantity =
        typeof currentInspection?.removedQuantity === 'number' &&
        typeof currentInspection?.sampleSize === 'number' &&
        currentInspection.removedQuantity > currentInspection.sampleSize;

    const actionButtons: ActionButtons = {
        secondaryButton: {
            label: 'Back',
            onClick: handleBackClick
        },
        previewButton: canOpenInspection ? {
            label: 'Preview',
            onClick: handlePreviewClick
        } : undefined,
    };
    if (canOpenInspection) {
        actionButtons.primaryButton = {
            label: isSubmitting ? 'Submitting...' : 'Submit',
            styleName: 'submit',
            onClick: handleSubmitClick,
            isDisabled: isSubmitting || !isModified || hasInvalidRemovedQuantity
        }
    }


    if (
        openLoadError &&
        openLoadError.inspectionId === id &&
        hasRole(ROLE.ADMINISTRATOR) &&
        (openLoadError.reason === 'offline_lite' || openLoadError.reason === 'fetch_failed')
    ) {
        const messageKey =
            openLoadError.reason === 'offline_lite'
                ? 'inspection_hydration_requires_connection'
                : 'inspection_start_failed';
        return (
            <section className={styles.scrollableContainer}>
                <div className={styles.loadErrorPanel} role="alert">
                    <p>{translate(messageKey)}</p>
                    <button
                        type="button"
                        onClick={() => {
                            dispatch(clearOpenInspectionLoadError());
                            navigate(ROUTES.INSPECTIONS, { replace: true });
                        }}
                    >
                        {translate('Back')}
                    </button>
                </div>
            </section>
        );
    }

    if (!template) return translate('Loading');
    return <section className={styles.scrollableContainer
    } >
        {!canOpenInspection && <LockedForUploadMessage />}
        {canOpenInspection && < div className={styles.scrollableItem} >
            {template?.modules?.map((module, index) => <div key={index}>{renderInspectionModule(module)}</div>)}

        </div >}
        {openDialog && <SaveChangesModal
            onClose={() => setOpenDialog(false)}
            onCancel={() => setOpenDialog(false)}
            onDiscard={handleDiscardChanges}
        />}
        {previewOpen && (
            <Preview
                inspection={currentInspection}
                onClose={() => setPreviewOpen(false)}
            />
        )}
        <Footer {...actionButtons} />
    </section >
}

